import firebase from "datastore/database";
import {
  flatten,
  flatMap,
  groupBy,
  isArray,
  isEqual,
  map,
  mapValues,
  maxBy,
  mergeWith,
  omitBy,
  uniq,
  uniqBy,
  values,
} from "lodash";

import { observeAllPages, observeCollection } from "api";
import { fbTsToDate } from "common/date";
import fetchDoc from "util/api/fetchDoc";
import { TIMELINE_TYPE } from "util/constants";

import fetchCustomerProfileData from "./fetchCustomerProfileData";

const getCustomer = async (businessUid, uid) =>
  fetchDoc(`business-customers/business/${businessUid}/${uid}`);

const getCustomerByPhone = async (businessUid, phoneNumber) => {
  if (!businessUid || !phoneNumber) return null;
  const snap = await firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .where("phoneNumber", "==", phoneNumber)
    .get();
  if (snap.empty) return null;
  return { uid: snap.docs[0].id, ...snap.docs[0].data() };
};

async function addCustomer(businessUid, customerData) {
  const doc = await firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .add({
      createdAt: new Date(),
      ...customerData,
    });
  logTimeline(businessUid, doc.id, {
    timestamp: new Date(),
    source: "restaurant",
    type: TIMELINE_TYPE.CUSTOMER_CREATED,
    customer: { ...customerData },
  });
  return { uid: doc.id, ...customerData };
}

const updateAndLog = async (businessUid, customer, customerData) => {
  try {
    await firebase
      .firestore()
      .collection(`business-customers/business/${businessUid}`)
      .doc(customer.uid)
      .update(customerData);

    await logTimeline(businessUid, customer?.uid, {
      timestamp: new Date(),
      source: "restaurant",
      type: TIMELINE_TYPE.UPDATE_CUSTOMER,
      old: customer,
      new: customerData,
    });
  } catch (error) {
    console.error("Failed to updateAndLog");
  }
};

const mergeTags = (oldTags, newTags) => {
  return uniqBy([...oldTags, ...newTags], "uid");
};

const setCustomer = async (
  businessUid,
  name = "No Name",
  phoneNumber,
  email,
  tags,
  nationality,
  dob,
  gender,
  customerProfileNotes
) => {
  let customerData = {
    ...(email && { email }),
    ...(tags && { tags }),
    ...(nationality && { nationality }),
    ...(dob && { dob }),
    ...(gender && { gender }),
    ...(customerProfileNotes != null && { customerProfileNotes }), // We want to write an empty string
    name,
    phoneNumber,
  };
  let customer = await getCustomerByPhone(businessUid, phoneNumber);
  if (!customer) return addCustomer(businessUid, customerData);

  delete customer.visits; // We don't want to log visits
  const customerTags = mergeTags(customer.tags || [], tags || []);
  customerData.tags = customerTags;
  if (!isEqual(customer, customerData)) {
    updateAndLog(businessUid, customer, customerData);
  }
  return { uid: customer.uid, ...customer, tags: customerTags };
};

const setCustomerWithoutUpdate = async (
  businessUid,
  name = "No Name",
  phoneNumber,
  email,
  tags,
  nationality,
  dob,
  gender,
  customerProfileNotes
) => {
  let customerData = {
    ...(email && { email }),
    ...(tags && { tags }),
    ...(nationality && { nationality }),
    ...(dob && { dob }),
    ...(gender && { gender }),
    ...(customerProfileNotes != null && { customerProfileNotes }), // We want to write an empty string
    name,
    phoneNumber,
  };
  let customer = await getCustomerByPhone(businessUid, phoneNumber);
  if (!customer) return addCustomer(businessUid, customerData);
  return { uid: customer.uid, ...customer, dob: fbTsToDate(customer.dob) };
};

const addOrGetCustomerFromDb = async (
  businessUid,
  name,
  phoneNumber,
  email,
  address
) => {
  let customer;
  const customerQuery = firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .where("phoneNumber", "==", phoneNumber);

  const snapshots = await customerQuery.get();
  if (snapshots.docs.length > 0) {
    const customerDoc = snapshots.docs[0];
    customer = {
      uid: customerDoc.id,
      ...customerDoc.data(),
      ...(email && { email }),
      ...(address && { address }),
    };
  } else {
    let customerData = {
      createdAt: new Date(),
      ...(name && { name }),
      phoneNumber,
      ...(email && { email }),
      ...(address && { address }),
    };
    const doc = await firebase
      .firestore()
      .collection(`business-customers/business/${businessUid}`)
      .doc();
    doc.set(customerData);
    customer = { uid: doc.id, ...customerData };
    logTimeline(businessUid, customer?.uid, {
      timestamp: new Date(),
      source: "restaurant",
      type: TIMELINE_TYPE.CUSTOMER_CREATED,
      customer: { ...customerData },
    });
  }
  return customer;
};

const updateCustomer = async (businessUid, customerUid, data) => {
  return firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .doc(customerUid)
    .set(data, { merge: true });
};

// data should have a timestamp, a source (either restaurant or customer), a type and some other data
const logTimeline = async (businessUid, customerUid, data) => {
  if (!businessUid || !customerUid) return;

  const timelineEntry = { ...data };
  // Add a timestamp and source automatically to simplify the API
  if (!timelineEntry.hasOwnProperty("timestamp")) {
    timelineEntry.timestamp = new Date();
  }
  if (!timelineEntry.hasOwnProperty("source")) {
    timelineEntry.source = "restaurant";
  }

  const cleanEntry = omitBy(timelineEntry, (val) => val === undefined);
  return firebase
    .firestore()
    .collection(`customer-log/${businessUid}/${customerUid}`)
    .add(cleanEntry);
};

async function getVisitsCount(businessUid, phoneNumber) {
  const snap = await firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .where("phoneNumber", "==", phoneNumber)
    .limit(1)
    .get();
  if (snap.empty) return 0;
  const count = snap?.docs?.[0]?.data()?.visitsCount ?? 0;
  return count;
}

async function searchByPhone(businessUid, phoneNumber) {
  const phone = phoneNumber.replace(/\+/g, "");
  const phonePlusOne = "+" + String(Number(phone) + 1);
  const snap = await firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .where("phoneNumber", ">=", phoneNumber)
    .where("phoneNumber", "<", phonePlusOne)
    .limit(5)
    .get();
  return snap.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
}

const getName = (group, currentBusiness) => {
  let obj = group.find((item) => item.businessUid === currentBusiness);
  if (obj) return obj.name;
  const mergedNames = uniq(map(group, "name").filter(Boolean));
  return mergedNames.length ? maxBy(mergedNames, (name) => name.length) : "";
};

const getUid = (group, currentBusiness) => {
  let obj = group.find((item) => item.businessUid === currentBusiness);
  if (obj) return obj.uid;
  return group[0].uid;
};
function normalizePhoneNumber(phoneNumber) {
  if (!phoneNumber) return "noPhoneNumber";
  // Remove all non-numeric characters from the phone number
  const numericPhoneNumber = phoneNumber.replace(/\D/g, "");
  return "+" + numericPhoneNumber;
}
function mergeDuplicates(currentBusiness, arr) {
  const grouped = groupBy(arr, "phoneNumber");
  const merged = mapValues(grouped, (group) => {
    const mergedTags = uniqBy(flatMap(group, "tags")?.filter(Boolean), "uid");
    const mergedBusinessNames = uniq(map(group, "businessUid").filter(Boolean));
    const mergedObj = mergeWith({}, ...group, (objValue, srcValue) => {
      if (isArray(objValue)) {
        return uniq(objValue.concat(srcValue));
      }
    });
    mergedObj.name = getName(group, currentBusiness);
    mergedObj.tags = mergedTags;
    mergedObj.businessNames = mergedBusinessNames;
    mergedObj.uid = getUid(group, currentBusiness);
    return mergedObj;
  });
  return values(merged);
}

async function populateCustomerSuggestions(businessUid, value, businessGroup) {
  if (businessGroup && businessGroup.uids.length > 0) {
    const promises = businessGroup.uids.map(async (businessUid) => {
      const customers = await searchByPhone(businessUid, value);
      return customers.map((el) => ({ ...el, businessUid }));
    });
    let customers = await Promise.all(promises);
    customers = flatten(customers);
    return mergeDuplicates(businessUid, customers);
  }
  return searchByPhone(businessUid, value);
}

const getStartEndString = (string) => {
  const strSearch = string;
  const strlength = strSearch.length;
  const strFrontCode = strSearch.slice(0, strlength - 1);
  const strEndCode = strSearch.slice(strlength - 1, strSearch.length);
  const startcode = strSearch;
  const endcode =
    strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1);
  return { startcode, endcode };
};

async function searchByName(businessUid, value) {
  const { startcode, endcode } = getStartEndString(value);
  const snap = await firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .where("name", ">=", startcode)
    .where("name", "<", endcode)
    .limit(5)
    .get();
  return snap.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
}

async function populateCustomerByNameSuggestions(
  businessUid,
  value,
  businessGroup
) {
  if (businessGroup && businessGroup.uids.length > 0) {
    const promises = businessGroup.uids.map(async (businessUid) => {
      const customers = await searchByName(businessUid, value);
      return customers.map((el) => ({ ...el, businessUid }));
    });
    let customers = await Promise.all(promises);
    customers = flatten(customers);

    return mergeDuplicates(businessUid, customers);
  }
  return searchByName(businessUid, value);
}

async function searchByEmail(businessUid, string) {
  const { startcode, endcode } = getStartEndString(string);
  const snap = await firebase
    .firestore()
    .collection(`business-customers/business/${businessUid}`)
    .where("email", ">=", startcode)
    .where("email", "<", endcode)
    .limit(5)
    .get();
  return snap.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
}

async function populateCustomerByEmailSuggestions(
  businessUid,
  value,
  businessGroup
) {
  if (businessGroup && businessGroup.uids.length > 0) {
    const promises = businessGroup.uids.map(async (businessUid) => {
      let customers = await searchByEmail(businessUid, value);
      return customers.map((el) => ({ ...el, businessUid }));
    });
    let customers = await Promise.all(promises);
    customers = flatten(customers);
    return mergeDuplicates(businessUid, customers);
  }
  return searchByEmail(businessUid, value);
}

async function fetchCustomers(dispatch, businessUid) {
  const path = `business-customers/business/${businessUid}`;
  const orderBy = "name";

  dispatch({ type: "ADD_CUSTOMERS_LOADING" });

  const callback = ({ added, modified, removed }) => {
    if (added?.length > 0) dispatch({ type: "ADD_CUSTOMERS", data: added });
    if (modified?.length > 0)
      dispatch({ type: "UPDATE_CUSTOMERS", data: modified });
    if (removed?.length > 0)
      dispatch({ type: "DELETE_CUSTOMERS", data: removed });
  };

  await observeAllPages(path, orderBy, callback);

  return observeCollection(path, callback);
}

export {
  addCustomer,
  addOrGetCustomerFromDb,
  fetchCustomers,
  fetchCustomerProfileData,
  getCustomer,
  getCustomerByPhone,
  getVisitsCount,
  logTimeline,
  populateCustomerByEmailSuggestions,
  populateCustomerByNameSuggestions,
  populateCustomerSuggestions,
  setCustomer,
  setCustomerWithoutUpdate,
  updateCustomer,
};
